/**
 * RevokeModal — archives an `approuve` lien and cascades the médecin's codes → inactif in one tx
 * (POST /referral/liens/:id/revoquer). Confirmation-style: a motif (optional, traced) + an explicit
 * destructive action. Gated by referral.lien.manage.
 */
import { useEffect, useId, useState } from "react";
import { Button, FormField, Modal, Textarea, useToast } from "@/ui";
import { useT } from "@/app";
import { useRevokeLien } from "../api";

export interface RevokeModalProps {
  open: boolean;
  labId: string | null;
  lienId: string | null;
  medecinNom?: string | undefined;
  disabled?: boolean;
  onClose: () => void;
  onRevoked?: () => void;
}

export function RevokeModal({
  open,
  labId,
  lienId,
  medecinNom,
  disabled,
  onClose,
  onRevoked,
}: RevokeModalProps) {
  const { t } = useT();
  const toast = useToast();
  const revoke = useRevokeLien(labId);
  const formId = useId();
  const [motif, setMotif] = useState("");

  useEffect(() => {
    if (open) setMotif("");
  }, [open]);

  const canSubmit = !disabled && !!lienId && !revoke.isPending;

  const submit = () => {
    if (!canSubmit || !lienId) return;
    revoke.mutate(
      { lienId, ...(motif.trim() ? { motif: motif.trim() } : {}) },
      {
        onSuccess: (res) => {
          toast.success(t("referral.revoke.success", { n: res.codes_desactives }));
          onRevoked?.();
          onClose();
        },
        onError: (err) => toast.error(err.message, t("referral.revoke.title")),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("referral.revoke.title")}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("referral.action.cancel")}
          </Button>
          <Button
            variant="danger"
            form={formId}
            type="submit"
            loading={revoke.isPending}
            disabled={!canSubmit}
          >
            {t("referral.revoke.submit")}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}
      >
        <p style={{ margin: 0, color: "var(--nv-slate)" }}>{t("referral.revoke.desc")}</p>
        {medecinNom && (
          <div>
            <span style={{ color: "var(--nv-slate)" }}>{t("referral.revoke.medecin")}: </span>
            <strong>{medecinNom}</strong>
          </div>
        )}
        <FormField label={t("referral.revoke.motif")} hint={t("referral.revoke.motif.hint")}>
          {(p) => (
            <Textarea
              {...p}
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
              autoFocus
            />
          )}
        </FormField>
      </form>
    </Modal>
  );
}
